import useSWR, { mutate } from 'swr';
import useSWRMutation from 'swr/mutation';
import {
    EditReviewDto,
    ReviewDeletePostDto,
    ReviewUpdatePostDto,
    SubjectMonitoringCloseReviewModel,
    SubjectMonitoringConfigForReviewDto,
    SubjectMonitoringReviewAddResponseModel,
    SubjectMonitoringReviewDto,
    SubjectMonitoringReviewPostDto,
    SubjectMonitoringUpdateReviewSummaryModel,
} from '@interfaces-clean/reviews.interface';
import { getData, postData } from '@apis/rest-client';

const timeout = 30000;
const urlPrefix = 'SubjectMonitoring/Review';

export const SubjectMonitoringReviewAPI = {
    useGetAllReviews: (studyRef: string, timestamp?: string) => {
        const getAllUrl = timestamp ? `${urlPrefix}/${studyRef}/${timestamp}` : `${urlPrefix}/${studyRef}`;
        return useSWRMutation(getAllUrl, (url: string) => {
            return getData<SubjectMonitoringReviewDto[]>(url, { timeout });
        });
    },

    useGetAllReviewsQuery: (studyRef: string, timestamp?: string) => {
        const getAllUrl = timestamp ? `${urlPrefix}/${studyRef}/${timestamp}` : `${urlPrefix}/${studyRef}`;
        return useSWR(getAllUrl, (url: string) => {
            return getData<SubjectMonitoringReviewDto[]>(url, { timeout });
        });
    },

    usePostReview: (studyRef: string) => {
        const postUrl = `${urlPrefix}/${studyRef}`;
        return useSWRMutation(postUrl, (url: string, { arg }: { arg: SubjectMonitoringReviewPostDto }) => {
            return postData<SubjectMonitoringReviewPostDto, SubjectMonitoringReviewAddResponseModel>(url, {
                arg,
                timeout,
            });
        });
    },

    useGetByRecordIdLazy: (studyRef: string, recordId: number) => {
        const getByRecordIdUrl = `${urlPrefix}/GetByRecordId/${studyRef}/${recordId}`;
        return useSWRMutation(getByRecordIdUrl, (url: string) => {
            return getData<EditReviewDto>(url, { timeout });
        });
    },

    useGetByRecordIdQuery: (studyRef: string, recordId: number | null | undefined) => {
        // Don't make query if `recordId` is not defined
        const key = recordId != null ? (`${urlPrefix}/GetByRecordId/${studyRef}/${recordId}` as const) : null;

        return useSWR(key, (url) => {
            return getData<EditReviewDto>(url, { timeout });
        });
    },

    useUpdateReview: (studyRef: string, recordId: number) => {
        const updateUrl = `${urlPrefix}/Update/${studyRef}/${recordId}`;
        return useSWRMutation(updateUrl, async (url: string, { arg }: { arg: ReviewUpdatePostDto }) => {
            const result = await postData<ReviewUpdatePostDto, SubjectMonitoringReviewAddResponseModel>(url, {
                arg,
                timeout,
            });

            // Get specific review by study/recordId
            // eslint-disable-next-line no-void
            void mutate(`${urlPrefix}/GetByRecordId/${studyRef}/${recordId}`);
            // Get all by study (no timestamp)
            // eslint-disable-next-line no-void
            void mutate(`${urlPrefix}/${studyRef}`);

            return result;
        });
    },

    useUpdateReviewSummaryMutation: (studyRef: string, recordId: number) => {
        const updateUrl = `${urlPrefix}/UpdateSummary/${studyRef}/${recordId}`;

        return useSWRMutation(updateUrl, async (url, { arg }: { arg: SubjectMonitoringUpdateReviewSummaryModel }) => {
            const success = await postData<SubjectMonitoringUpdateReviewSummaryModel, boolean>(url, {
                arg,
                timeout,
            });

            if (success) {
                /* eslint-disable no-void */
                // Invalidate queries which involve updated review
                // Get specific review by study/recordId
                void mutate(`${urlPrefix}/GetByRecordId/${studyRef}/${recordId}`);
                // Get all by study (no timestamp)
                void mutate(`${urlPrefix}/${studyRef}`);
                /* eslint-enable no-void */
            }
            return success;
        });
    },

    // Returns uuid (sessionId)
    useOpenReviewMutation: (studyRef: string) => {
        const key = ['OpenReview', studyRef];

        return useSWRMutation(key, (_, { arg: reviewRecordId }: { arg: number }) => {
            const url = `${urlPrefix}/${studyRef}/${reviewRecordId}/OpenReview`;

            return postData<undefined, string>(url, {
                arg: undefined,
                timeout,
            });
        });
    },

    useCloseReviewMutation: (studyRef: string, reviewRecordId: number) => {
        const key = ['CloseReview', studyRef, reviewRecordId] as const;

        return useSWRMutation(key, (_, { arg }: { arg: SubjectMonitoringCloseReviewModel }) => {
            const url = `${urlPrefix}/${studyRef}/${reviewRecordId}/CloseReview`;

            return postData(url, { arg, timeout });
        });
    },

    useGetConfigForReview: (studyRef: string | null, reviewRecordId: number | null | undefined) => {
        const key =
            studyRef && reviewRecordId != null ? (`${urlPrefix}/${studyRef}/${reviewRecordId}/Config` as const) : null;

        return useSWR(key, async (url) => {
            const result = await getData<SubjectMonitoringConfigForReviewDto | ''>(url, { timeout });

            // For some reason, when a NoContent response is returned, it gets converted into ''...
            if (result === '') {
                return undefined;
            }

            return result;
        });
    },

    useDeleteReview: (studyRef: string, recordId: number | null | undefined) => {
        const key = recordId != null ? `${urlPrefix}/Delete/${studyRef}/${recordId}` : null;

        return useSWRMutation(key, (url, { arg }: { arg: ReviewDeletePostDto }) => {
            return postData<ReviewDeletePostDto, void>(url, {
                arg,
                timeout,
            });
        });
    },

    useGetLastReviewedDatasetIdForReviewPlanForDataListingForSubjectQuery: (
        studyRef: string,
        reviewPlanId: number | null | undefined,
        subjectId: number,
        dataListingId: string | null | undefined,
    ) => {
        const key =
            reviewPlanId != null && dataListingId != null
                ? `${urlPrefix}/${studyRef}/${reviewPlanId}/${subjectId}/${dataListingId}/GetLastReviewedDatasetIdForReviewPlanForDataListingForSubject`
                : null;

        return useSWR(key, (url) => {
            return getData<number | undefined>(url, { timeout });
        });
    },
} as const;
